import { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import axios from 'axios';
import rateLimit from 'axios-rate-limit';
import {
  FileArrowDown,
  FileEarmarkPlus,
  FolderSymlink,
  SortAlphaDown,
  SortAlphaUp,
} from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Navbar';
import Table from 'react-bootstrap/Table';
import { Storage } from 'aws-amplify';
import internalStatuses from '../enumerations/internalProductStatuses';
import partnerStatuses from '../enumerations/partnerProductStatuses';
import collectionsSubcollections from '../enumerations/collectionsSubcollections';
import getProductCollections from '../utils/getProductCollections';
import { Form } from 'react-bootstrap';
import { CSVLink } from 'react-csv';

// sets max 10 requests per 1 second
const http = rateLimit(axios.create(), { maxRequests: 1000, perMilliseconds: 1000 })

export const ProductsPage = () => {
  const { collection, productType, searchString, noCacheStart, noCacheEnd } =
    useParams();
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [productsNotes, setProductsNotes] = useState([]);
  const [sortOrder, setSortOrder] = useState(
    window.localStorage?.getItem('productSortOrder') || null
  );
  const [filterProductStatus, setFilterProductStatus] = useState(
    window.localStorage?.getItem('filterProductStatus') || ''
  );
  let localFilterSubcollection = window.localStorage?.getItem(
    'filterSubcollection'
  ) || '';
  if (localFilterSubcollection === 'null') {
    localFilterSubcollection = '';
  }
  const [filterSubcollection, setFilterSubcollection] = useState(
    localFilterSubcollection
  );
  const history = useHistory(); // for routing
  const showAddProduct =
    authState.userPermissions?.[collection]?.create ||
    authState?.userGroups?.includes('facecake/admin');
  const showMoveCheckboxes = authState?.userGroups?.includes('facecake/admin');
  const [productSKUsToMove, setProductSKUsToMove] = useState([]);
  const [moveCheckboxAllSelected, setMoveCheckboxAllSelected] = useState(false);
  const statuses = collection === 'facecake' ? internalStatuses : partnerStatuses;
  const subcollections = collectionsSubcollections[collection];
  let csvData = [];

  useEffect(() => {
    async function fetchData() {
      let productSKUs;
      let productCollections;
      let axiosGets;
      // XXX: get all available collections and search for searchString
      if (searchString) {
        const userPermissions = authState?.userPermissions;
        const permittedCollections = Object.keys(userPermissions);
        productCollections = await getProductCollections(permittedCollections);
        axiosGets = productCollections.flatMap((productCollection) => {
          const collection = Object.keys(productCollection)[0];
          const { productTypes } = productCollection[collection];
          const getURLs = productTypes.map((productType) => {
            return productCollection[collection][productType].productSKUs
              .filter((productSKU) =>
                productSKU.includes(
                  searchString.replace(/[^0-9a-z]/gi, '').toLowerCase()
                )
              )
              .map((productSKU) => {
                return `https://cdn-nocache.facecake.com/p/${collection}/${productSKU}/metadata/index.json`;
              });
          });
          return getURLs.flat();
        });
      // XXX: else get products2.json for speed
      } else if (
        ['chanel'].includes(collection) &&
        !noCacheStart
      ) {
        const res = await Storage.get(`p/${collection}/products2.json`, {
          download: true,
          cacheControl: 'no-cache',
        });
        const resText = await res.Body.text();
        const products2 = JSON.parse(resText)[productType]?.products;
        setProducts(products2);
        setLoading(false);
        // XXX: else get the list of products for this collection
      } else if (collection !== 'ebay') { // HACK: exclude eBay
        const res = await Storage.get(`p/${collection}/products.json`, {
          download: true,
          cacheControl: 'no-cache',
        });
        const resText = await res.Body.text();
        productSKUs = JSON.parse(resText)[productType]?.productSKUs;
        axiosGets = productSKUs
          ?.filter(
            (val, i) => i >= (noCacheStart ?? 0) && i < (noCacheEnd ?? 9999)
          )
          ?.map(
            (productSKU) =>
              `https://cdn-nocache.facecake.com/p/${collection}/${productSKU}/metadata/index.json`
          );
      } else {
        axiosGets = [];
      }
      const getAll = async (collection) => {
        const requests = collection.map((req) => {
          return http.get(req).catch(() => null); // use http instead of axios
        });
        return Promise.all(requests);
      };
      if (axiosGets) {
        getAll(axiosGets)
          .then((responses) => {
            const products = responses
              .filter((res) => res?.status === 200)
              .map((res) => res.data);
            authState?.email?.includes('@facecake.com') &&
              console.log(JSON.stringify(products, null, 2));
            setProducts(products);
            setLoading(false);
          })
          .catch((err) => {
            console.warn(err);
            setLoading(false);
          });
      }

      /* FIXME: don't check history right now for speed
      const axiosHistoryGets = productSKUs?.map(
        (productSKU) =>
          `https://cdn-nocache.facecake.com/p/${collection}/${productSKU}/metadata/history.json`
      );
      getAll(axiosHistoryGets)
        .then((responses) => {
          const productHistories = responses
            .filter((res) => res?.status === 200)
            .map((res) => {
              const thisSKU = res.config.url
                ?.split(`/p/${collection}/`)[1]
                ?.split("/metadata")[0];
              const {notes, internalNotes} = res.data;
              return {sku: thisSKU, notes, internalNotes};
            });
          setProductsNotes(productHistories);
        })
        .catch((err) => {
          console.warn(err);
        }); */
    }
    fetchData();

    // if filterProductStatus is not in current statuses, set to null
    if (statuses.includes(filterProductStatus) === false) {
      setFilterProductStatus(null);
    }
  }, [
    collection,
    productType,
    searchString,
    sortOrder,
    filterProductStatus,
    filterSubcollection,
    statuses,
    authState?.userPermissions,
  ]);

  // XXX: sort products
  if (sortOrder) {
    products.sort((a, b) => {
      let field, direction;
      switch (sortOrder) {
        case 'brandAsc':
          field = 'brand';
          direction = 1;
          break;
        case 'brandDesc':
          field = 'brand';
          direction = -1;
          break;
        case 'nameAsc':
          field = 'name';
          direction = 1;
          break;
        case 'nameDesc':
          field = 'name';
          direction = -1;
          break;
        case 'skuAsc':
          field = 'sku';
          direction = 1;
          break;
        case 'skuDesc':
          field = 'sku';
          direction = -1;
          break;
        case 'statusAsc':
          field = collection === 'facecake' ? 'internalStatus' : 'status';
          direction = 1;
          break;
        case 'statusDesc':
          field = collection === 'facecake' ? 'internalStatus' : 'status';
          direction = -1;
          break;
        default:
          break;
      }
      let aFieldValue = a[field]?.toLowerCase();
      let bFieldValue = b[field]?.toLowerCase();
      if (['internalStatus', 'status'].includes(field) && !aFieldValue) {
        aFieldValue = 'new';
      }
      if (['internalStatus', 'status'].includes(field) && !bFieldValue) {
        bFieldValue = 'new';
      }
      if (aFieldValue < bFieldValue) {
        return -1 * direction;
      }
      if (aFieldValue > bFieldValue) {
        return 1 * direction;
      }
      return 0;
    });
  }

  // write filtered and sorted list of product skus to localStorage
  const currentSKUList = products
    .filter((product, index) => {
      if (filterProductStatus) {
        let thisStatus =
          collection === 'facecake' ? product.internalStatus : product.status;
        if (!thisStatus) {
          thisStatus = 'NEW';
        }
        return thisStatus.toLowerCase() === filterProductStatus.toLowerCase();
      } else {
        return true;
      }
    })
    .filter((product) => {
      if (filterSubcollection) {
        return product?.subcollections?.includes(filterSubcollection);
      } else {
        return true;
      }
    })
    .map((product) => product.sku);
  if (currentSKUList.length) {
    window.localStorage.setItem(
      'currentSKUList',
      JSON.stringify(currentSKUList)
    );
    // write data for CSV export
    try {
      csvData = currentSKUList.map((el, index) => {
        try {
          return ['', el.toUpperCase(), true, ''];
        } catch (error) {
          if (authState?.email?.includes('@facecake.com')) {
            console.error('CSV export map failed', {
              index,
              element: el,
              currentSKUList,
              error,
            });
          }
          throw error;
        }
      });
    } catch (e) {
      console.log(currentSKUList);
      console.log(272, e);
    }
  }

  // XXX: write to localStorage when updating sort order
  const updateSortOrder = (sortOrder) => {
    window.localStorage.setItem('productSortOrder', sortOrder);
    setSortOrder(sortOrder);
  };
  // XXX: write to localStorage when updating status filter
  const handleFilterProductStatusChange = (e) => {
    let filterProductStatus = e.target.value;
    window.localStorage.setItem('filterProductStatus', filterProductStatus);
    setFilterProductStatus(
      filterProductStatus === 'Show All' ? null : filterProductStatus
    );
  };
  // XXX: write to localStorage when updating status filter
  const handleFilterSubcollectionChange = (e) => {
    let filterSubcollection = e.target.value;
    if (filterSubcollection === 'Show All') {
      filterSubcollection = null;
    }
    window.localStorage.setItem('filterSubcollection', filterSubcollection);
    setFilterSubcollection(filterSubcollection);
  };
  // XXX: if move checkbox changed, add or remove from array
  const handleMoveCheckbox = (e) => {
    const sku = e.target.id.split('move-checkbox-')[1];
    const checked = e.target.checked;
    if (checked) {
      setProductSKUsToMove([...productSKUsToMove, sku]);
    } else {
      setProductSKUsToMove([...productSKUsToMove.filter((el) => el !== sku)]);
    }
  };
  const handleMoveCheckboxAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setProductSKUsToMove([...currentSKUList]);
      setMoveCheckboxAllSelected(true);
    } else {
      setProductSKUsToMove([]);
      setMoveCheckboxAllSelected(false);
    }
  };

  return (
    <Container fluid className="px-0 px-sm-1">
      <Row className="px-0 px-sm-3">
        <Col className="px-0 px-sm-3">
          <nav aria-label="breadcrumb">
            <div className="float-right py-1 pr-2 add-product-breadcrumb-fix">
              {showAddProduct && (
                <Button
                  className="light-button"
                  onClick={() =>
                    history.push(
                      `/product/add/${collection}${
                        productType ? `/${productType}` : ''
                      }`
                    )
                  }
                >
                  <FileEarmarkPlus />
                </Button>
              )}
              &nbsp;
              <Button className="light-button">
                <CSVLink
                  data={csvData}
                  separator={';'}
                  enclosingCharacter={''}
                  filename={`${collection}-${productType}${
                    filterProductStatus ? `-${filterProductStatus}` : ''
                  }-${new Date().toISOString().split('T')[0]}.csv`}
                >
                  <FileArrowDown className="pointer" />
                </CSVLink>
              </Button>
            </div>

            <ol className="breadcrumb">
              {searchString ? (
                <>
                  <li className="breadcrumb-item">
                    <a href={'/'}>SEARCH</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {searchString.toUpperCase()}&nbsp;({currentSKUList?.length})
                  </li>
                </>
              ) : (
                <>
                  <li className="breadcrumb-item">
                    <a href={`/products/${collection}`}>
                      {collection?.toUpperCase()}
                    </a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {productType?.toUpperCase()}&nbsp;({currentSKUList?.length})
                  </li>
                  <li className="pl-2" style={{ marginTop: '-4px' }}>
                    <Form>
                      <Form.Group
                        className="my-0 labeled-dropdown-container"
                        controlId="filterProductStatus"
                      >
                        <Form.Label className="labeled-dropdown-label">
                          Status:
                        </Form.Label>
                        <Form.Control
                          as="select"
                          size="sm"
                          className="py-0 labeled-dropdown-select"
                          custom
                          value={
                            filterProductStatus
                              ? filterProductStatus
                              : 'Show All'
                          }
                          onChange={handleFilterProductStatusChange}
                        >
                          <option>Show All</option>
                          {statuses.map((status) => (
                            <option key={status}>{status}</option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Form>
                  </li>
                  {subcollections?.length && (
                    <li className="pl-2" style={{ marginTop: '-4px' }}>
                      <Form>
                        <Form.Group
                          className="my-0 labeled-dropdown-container"
                          controlId="filterSubcollection"
                        >
                          <Form.Label className="labeled-dropdown-label">
                            Collection:
                          </Form.Label>
                          <Form.Control
                            as="select"
                            size="sm"
                            className="py-0 labeled-dropdown-select"
                            custom
                            value={
                              filterSubcollection
                                ? filterSubcollection
                                : 'Show All'
                            }
                            onChange={handleFilterSubcollectionChange}
                          >
                            <option>Show All</option>
                            {subcollections.map((subcollection) => (
                              <option key={subcollection}>
                                {subcollection}
                              </option>
                            ))}
                          </Form.Control>
                        </Form.Group>
                      </Form>
                    </li>
                  )}
                </>
              )}
            </ol>
          </nav>
        </Col>
      </Row>
      <Row className="px-0 px-sm-3">
        <Col className="px-0 px-sm-3">
          <Table size="sm" hover striped>
            <thead>
              <tr>
                {showMoveCheckboxes && (
                  <th>
                    <Form.Check
                      inline
                      checked={moveCheckboxAllSelected}
                      label={
                        <>
                          <FolderSymlink
                            className="pointer"
                            onClick={() => {
                              history.push({
                                pathname: `/move/${collection}/${productType}`,
                                data: { productSKUsToMove: productSKUsToMove },
                              });
                            }}
                          />
                        </>
                      }
                      type="checkbox"
                      id={`move-checkbox-all`}
                      onChange={handleMoveCheckboxAll}
                    />
                  </th>
                )}
                <th></th>
                <th>
                  Brand &nbsp; &nbsp;
                  <SortAlphaDown
                    className="pointer"
                    onClick={() => updateSortOrder('brandAsc')}
                    color={sortOrder === 'brandAsc' ? 'black' : 'lightgray'}
                  />
                  &nbsp;
                  <SortAlphaUp
                    className="pointer"
                    onClick={() => updateSortOrder('brandDesc')}
                    color={sortOrder === 'brandDesc' ? 'black' : 'lightgray'}
                  />
                </th>
                <th>
                  Product &nbsp; &nbsp;
                  <SortAlphaDown
                    className="pointer"
                    onClick={() => updateSortOrder('nameAsc')}
                    color={sortOrder === 'nameAsc' ? 'black' : 'lightgray'}
                  />
                  &nbsp;
                  <SortAlphaUp
                    className="pointer"
                    onClick={() => updateSortOrder('nameDesc')}
                    color={sortOrder === 'nameDesc' ? 'black' : 'lightgray'}
                  />
                </th>
                <th>
                  SKU &nbsp; &nbsp;
                  <SortAlphaDown
                    className="pointer"
                    onClick={() => updateSortOrder('skuAsc')}
                    color={sortOrder === 'skuAsc' ? 'black' : 'lightgray'}
                  />
                  &nbsp;
                  <SortAlphaUp
                    className="pointer"
                    onClick={() => updateSortOrder('skuDesc')}
                    color={sortOrder === 'skuDesc' ? 'black' : 'lightgray'}
                  />
                </th>
                <th>
                  Status &nbsp; &nbsp;
                  <SortAlphaDown
                    className="pointer"
                    onClick={() => updateSortOrder('statusAsc')}
                    color={sortOrder === 'statusAsc' ? 'black' : 'lightgray'}
                  />
                  &nbsp;
                  <SortAlphaUp
                    className="pointer"
                    onClick={() => updateSortOrder('statusDesc')}
                    color={sortOrder === 'statusDesc' ? 'black' : 'lightgray'}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {products.length ? (
                products
                  .filter((product) => {
                    if (filterProductStatus && !searchString) {
                      let thisStatus =
                        collection === 'facecake'
                          ? product.internalStatus
                          : product.status;
                      if (!thisStatus) {
                        thisStatus = 'NEW';
                      }
                      return (
                        thisStatus.toLowerCase() ===
                        filterProductStatus.toLowerCase()
                      );
                    } else {
                      return true;
                    }
                  })
                  .filter((product) => {
                    if (filterSubcollection) {
                      return product?.subcollections?.includes(
                        filterSubcollection
                      );
                    } else {
                      return true;
                    }
                  })
                  .map(function (product) {
                    if (product.sku) {
                      const handleClick = () =>
                        history.push(
                          `/product/${
                            collection || product?.brand?.toLowerCase()
                          }/${
                            productType || product?.productType?.toLowerCase()
                          }/${product.sku}`
                        );
                      return (
                        <tr key={product.sku} className="product-list-row">
                          {showMoveCheckboxes && (
                            <td>
                              <Form.Check
                                inline
                                checked={productSKUsToMove.includes(
                                  product.sku
                                )}
                                type="checkbox"
                                id={`move-checkbox-${product.sku}`}
                                onChange={handleMoveCheckbox}
                              />
                              {productsNotes.find(
                                (el) => el.sku === product.sku
                              )?.notes?.length > 0
                                ? '*'
                                : ''}
                            </td>
                          )}
                          <td onClick={handleClick}>
                            {product?.images?.[0]?.url && (
                              <img
                                src={product?.images?.[0]?.url}
                                style={{ height: '48px' }}
                                alt="product thumbnail"
                              />
                            )}
                          </td>
                          <td onClick={handleClick}>{product?.brand}</td>
                          <td onClick={handleClick}>{product?.name}</td>
                          <td onClick={handleClick}>
                            {product?.sku?.toUpperCase()}
                          </td>
                          <td onClick={handleClick}>
                            {collection === 'facecake'
                              ? product?.internalStatus || 'NEW'
                              : product?.status || 'NEW'}
                          </td>
                        </tr>
                      );
                    } else {
                      return null;
                    }
                  })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    {loading ? 'Loading...' : 'NO RESULTS FOUND'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}
